/**
 * PatientLookupSlider — compact right-side quick-view panel used from
 * Scheduling, Inbox, Leads, Call Tracker, Front Desk Tasks, Patients list
 * and the ⌘K palette.
 *
 * Design:
 *   - Small footer of phone/email chips beside last-active timestamp
 *   - Prominent chips for Front Desk Team + Patient Status
 *   - Next Appointment shows PT (practitioner) name inline
 *   - "Edit Profile" button switches to the FULL editable PatientDetailPanel
 *   - "Open Full Profile" navigates to the standalone /patients/:id page
 *   - Tags removed (they belong on the full profile page)
 *
 * Data source: /api/patients/{contactId}/lookup — Mongo-first, with GHL
 * hydration fallback for cold contacts.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X, Loader2, ExternalLink, ArrowRight, Cake, MapPin, Calendar,
  CheckCircle2, AlertCircle, Activity, MessageSquare, PhoneCall,
  ClipboardCheck, Edit3, Users2, Stethoscope, UserMinus,
} from 'lucide-react';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '../ui/sheet';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { toast } from 'sonner';
import { patientApi } from '../../lib/patientApi';
import { cn } from '../../lib/utils';
import { UnsubscribeDialog } from './UnsubscribeDialog';
import { useAuth } from '../../contexts/AuthContext';

function fmtDate(iso, opts = {}) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: opts.time ? 'numeric' : undefined,
      minute: opts.time ? '2-digit' : undefined,
    });
  } catch { return String(iso).slice(0, 19); }
}

function ageFromDob(dob) {
  if (!dob) return null;
  try {
    const b = new Date(dob);
    if (isNaN(b.getTime())) return null;
    const now = new Date();
    let age = now.getFullYear() - b.getFullYear();
    const m = now.getMonth() - b.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age--;
    return age;
  } catch { return null; }
}

function StatChip({ label, value, sub, tone = 'slate', testId }) {
  const tones = {
    slate: 'bg-slate-50 border-slate-200 text-slate-700',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-800',
  };
  return (
    <div className={cn('rounded-lg border px-3 py-2', tones[tone] || tones.slate)} data-testid={testId}>
      <div className="text-[9.5px] uppercase tracking-wider font-bold opacity-70">{label}</div>
      <div className="text-[15px] font-bold leading-tight mt-0.5">{value ?? '—'}</div>
      {sub && <div className="text-[10px] opacity-60 mt-0.5">{sub}</div>}
    </div>
  );
}

function StatusBadge({ ok, labelOk, labelNo, testId }) {
  return (
    <div
      className={cn(
        'flex items-center gap-1.5 px-2 py-1 rounded-md text-[10.5px] font-semibold',
        ok
          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
          : 'bg-slate-50 text-slate-500 border border-slate-200'
      )}
      data-testid={testId}
    >
      {ok ? <CheckCircle2 size={11} /> : <AlertCircle size={11} />}
      {ok ? labelOk : labelNo}
    </div>
  );
}

export function PatientLookupSlider({ open, onOpenChange, contactId, locationId, initialName, onEdit }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [showUnsubscribe, setShowUnsubscribe] = useState(false);
  const navigate = useNavigate();
  const { hasPermission, user: authUser } = useAuth();
  const canUnsubscribe = !!authUser?.is_super_admin || hasPermission('unsubscribe', 'apply') || hasPermission('unsubscribe', 'manage');

  useEffect(() => {
    if (!open || !contactId || !locationId) return;
    let cancelled = false;
    setLoading(true);
    setData(null);
    (async () => {
      try {
        const r = await patientApi.lookup(contactId, locationId);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) toast.error('Failed to load patient: ' + (e?.response?.data?.detail || e.message));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, contactId, locationId]);

  const openFullProfile = () => {
    if (!contactId) return;
    onOpenChange(false);
    navigate(`/patients/${contactId}`);
  };

  const openEditMode = () => {
    if (!contactId) return;
    onEdit?.(contactId, data?.name || initialName, {
      phone: data?.phone, email: data?.email,
      dateAdded: data?.createdAt, direction: '', duration: 0,
    });
  };

  const openJuvonno = () => {
    if (!data?.juvonno_id || !data?.juvonno_portal_url) {
      toast.error('Juvonno not linked for this patient');
      return;
    }
    const url = `${data.juvonno_portal_url.replace(/\/$/, '')}/patient/profile.php?txtCustomerID=${data.juvonno_id}`;
    window.open(url, 'juvonno-window');
  };

  const age = ageFromDob(data?.dateOfBirth);
  const displayName = data?.name || initialName || 'Patient';
  const nextApt = data?.next_appointment;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-[440px] sm:max-w-[460px] p-0 overflow-y-auto"
        data-testid="patient-lookup-slider"
      >
        <VisuallyHidden>
          <SheetTitle>Patient Lookup — {data?.name || initialName || 'Loading'}</SheetTitle>
          <SheetDescription>
            Quick overview with contact chips, front desk assignment, next appointment, intake and recent activity.
          </SheetDescription>
        </VisuallyHidden>

        {loading || !data ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="animate-spin h-5 w-5 text-slate-400" />
          </div>
        ) : (
          <div className="flex flex-col">
            {/* Header */}
            <div className="px-4 py-3 border-b border-slate-200 bg-gradient-to-b from-white to-slate-50/40">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2
                      className="text-[17px] font-bold text-slate-900 truncate"
                      data-testid="lookup-patient-name"
                    >
                      {displayName}
                    </h2>
                    {data.is_returning && (
                      <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 text-[9.5px] font-semibold h-5">
                        Returning
                      </Badge>
                    )}
                  </div>
                  {/* Small phone / email / age / last-active row */}
                  <div className="flex items-center gap-2 text-[10.5px] text-slate-500 mt-1 flex-wrap">
                    {age !== null && <span className="flex items-center gap-1"><Cake size={9.5} />{age}y</span>}
                    {data.gender && <span>{data.gender}</span>}
                    {data.phone && (
                      <a
                        href={`tel:${data.phone}`}
                        className="hover:text-indigo-600 truncate max-w-[120px]"
                        data-testid="lookup-phone-inline"
                        title={data.phone}
                      >{data.phone}</a>
                    )}
                    {data.email && (
                      <a
                        href={`mailto:${data.email}`}
                        className="hover:text-indigo-600 truncate max-w-[160px]"
                        data-testid="lookup-email-inline"
                        title={data.email}
                      >{data.email}</a>
                    )}
                    {data.last_active && (
                      <span className="flex items-center gap-1 text-slate-400">
                        <Activity size={9} />{fmtDate(data.last_active)}
                      </span>
                    )}
                  </div>

                  {/* Front Desk Team + Patient Status pills */}
                  <div className="flex items-center gap-1.5 mt-2 flex-wrap" data-testid="lookup-fd-pills">
                    {data.fd_team_member && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-indigo-50 border border-indigo-200 text-indigo-700 text-[10px] font-semibold">
                        <Users2 size={9} /> {data.fd_team_member}
                      </span>
                    )}
                    {data.patient_status && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-violet-50 border border-violet-200 text-violet-700 text-[10px] font-semibold">
                        {data.patient_status}
                      </span>
                    )}
                    {data.new_patient_status && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-700 text-[10px] font-semibold">
                        {data.new_patient_status}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => onOpenChange(false)}
                  className="p-1 rounded hover:bg-slate-100 shrink-0 sr-only"
                  aria-hidden="true"
                  tabIndex={-1}
                  data-testid="lookup-close-btn"
                >
                  <X size={16} className="text-slate-500" />
                </button>
              </div>

              {/* Primary CTAs */}
              <div className="flex gap-1.5 mt-3">
                <Button
                  size="sm"
                  className="flex-1 h-8 text-[11.5px] bg-slate-900 hover:bg-slate-800"
                  onClick={openFullProfile}
                  data-testid="lookup-open-profile-btn"
                >
                  Open Full Profile <ArrowRight size={12} className="ml-1.5" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 px-2.5 text-[11.5px] border-violet-300 text-violet-700 hover:bg-violet-50"
                  onClick={openEditMode}
                  data-testid="lookup-edit-btn"
                  title="Edit patient info + notes + tasks"
                >
                  <Edit3 size={12} className="mr-1" /> Edit
                </Button>
                {data.juvonno_id && data.juvonno_portal_url && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 text-[11.5px] border-teal-300 text-teal-700 hover:bg-teal-50"
                    onClick={openJuvonno}
                    data-testid="lookup-juvonno-btn"
                    title={`Open Juvonno #${data.juvonno_id}`}
                  >
                    <ExternalLink size={12} className="mr-1" /> Juv #{data.juvonno_id}
                  </Button>
                )}
                {canUnsubscribe && contactId && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 px-2.5 text-[11.5px] border-rose-300 text-rose-700 hover:bg-rose-50"
                    onClick={() => setShowUnsubscribe(true)}
                    data-testid="lookup-unsubscribe-btn"
                    title="Unsubscribe from campaigns"
                  >
                    <UserMinus size={12} className="mr-1" /> Unsubscribe
                  </Button>
                )}
              </div>
            </div>

            {/* DOB + Address as compact rows (no big cards) */}
            <div className="px-4 py-3 space-y-1.5 border-b border-slate-100" data-testid="lookup-contact-section">
              <div className="flex items-center gap-2 text-[11.5px] text-slate-700">
                <Cake size={11} className="text-slate-400 shrink-0" />
                <span className="text-slate-400 uppercase tracking-wider text-[9px] font-bold">DOB</span>
                <span className="font-medium">
                  {data.dateOfBirth || <span className="italic text-slate-400">—</span>}
                  {age !== null && <span className="text-slate-400 ml-1">({age} yrs)</span>}
                </span>
              </div>
              <div className="flex items-center gap-2 text-[11.5px] text-slate-700">
                <MapPin size={11} className="text-slate-400 shrink-0" />
                <span className="text-slate-400 uppercase tracking-wider text-[9px] font-bold">Addr</span>
                <span className="font-medium truncate">
                  {[data.address?.line1, data.address?.city, data.address?.state, data.address?.postalCode]
                    .filter(Boolean).join(', ') || <span className="italic text-slate-400">—</span>}
                </span>
              </div>
            </div>

            {/* Statistics */}
            <div className="px-4 py-3" data-testid="lookup-stats-section">
              <div className="text-[9.5px] font-bold uppercase tracking-wider text-slate-500 mb-2">
                Statistics
              </div>
              <div className="grid grid-cols-2 gap-2">
                <StatChip
                  label="Total Apts"
                  value={data.total_appointments ?? 0}
                  tone="indigo"
                  testId="lookup-stat-total-apts"
                />
                <StatChip
                  label="Cancelled/Missed"
                  value={data.cancelled_missed_appointments ?? 0}
                  tone={data.cancelled_missed_appointments > 0 ? 'amber' : 'slate'}
                  testId="lookup-stat-missed"
                />
              </div>

              {/* Next appointment — with PT prominently displayed */}
              <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3" data-testid="lookup-next-apt">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">
                  <Calendar size={11} /> Next Appointment
                </div>
                {nextApt ? (
                  <div>
                    <div className="text-[12.5px] font-semibold text-slate-800">
                      {nextApt.title || 'Appointment'}
                    </div>
                    <div className="text-[11px] text-slate-600 mt-0.5">
                      {fmtDate(nextApt.start_time, { time: true })}
                    </div>
                    {nextApt.staff && (
                      <div
                        className="inline-flex items-center gap-1 mt-1.5 px-1.5 py-0.5 rounded bg-blue-50 border border-blue-200 text-blue-700 text-[10.5px] font-semibold"
                        data-testid="lookup-next-apt-pt"
                      >
                        <Stethoscope size={9.5} /> PT: {nextApt.staff}
                      </div>
                    )}
                    {nextApt.notes && (
                      <div className="text-[10.5px] text-amber-800 mt-1.5 bg-amber-50 border border-amber-100 rounded px-1.5 py-1 leading-snug">
                        {nextApt.notes.slice(0, 120)}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-[11px] text-slate-400 italic">No upcoming appointment</div>
                )}
              </div>

              {/* Last visit */}
              {data.last_appointment && (
                <div className="mt-2 rounded-lg border border-slate-200 bg-white p-3" data-testid="lookup-last-apt">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">
                    <Calendar size={11} /> Last Visit
                  </div>
                  <div className="text-[12px] font-medium text-slate-700">
                    {data.last_appointment.title || 'Visit'}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {fmtDate(data.last_appointment.start_time, { time: true })}
                    {data.last_appointment.staff && (
                      <span className="text-blue-700 ml-1.5">· {data.last_appointment.staff}</span>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Intake & Consent */}
            <div className="px-4 py-3 border-t border-slate-100" data-testid="lookup-intake-section">
              <div className="text-[9.5px] font-bold uppercase tracking-wider text-slate-500 mb-2">
                Intake & Consent
              </div>
              <div className="grid grid-cols-2 gap-2">
                <StatusBadge
                  ok={data.intake_consent?.intake_completed}
                  labelOk="Intake Completed"
                  labelNo="Intake Pending"
                  testId="lookup-intake-status"
                />
                <StatusBadge
                  ok={data.intake_consent?.consent_completed}
                  labelOk="Consent Completed"
                  labelNo="Consent Pending"
                  testId="lookup-consent-status"
                />
              </div>
              {data.intake_form_status && (
                <div className="text-[10.5px] text-slate-500 mt-1.5">
                  <ClipboardCheck size={10} className="inline mr-1" />
                  Status: <span className="font-medium text-slate-700">{data.intake_form_status}</span>
                </div>
              )}
            </div>

            {/* Recent activity */}
            <div className="px-4 py-3 border-t border-slate-100 pb-4" data-testid="lookup-activity-section">
              <div className="text-[9.5px] font-bold uppercase tracking-wider text-slate-500 mb-2">
                Recent Activity
              </div>
              <div className="space-y-1.5">
                {data.last_message && data.last_message.at && (
                  <div className="flex items-start gap-2 rounded-md bg-slate-50 border border-slate-200/70 p-2">
                    <MessageSquare size={12} className="text-blue-500 shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] text-slate-500 flex items-center gap-1.5">
                        <span className="uppercase font-semibold">
                          {data.last_message.type || 'Message'}
                        </span>
                        <span>· {fmtDate(data.last_message.at)}</span>
                      </div>
                      <div className="text-[11.5px] text-slate-700 mt-0.5 line-clamp-2">
                        {data.last_message.body || <span className="italic text-slate-400">(no preview)</span>}
                      </div>
                    </div>
                  </div>
                )}
                {data.last_call && data.last_call.at && (
                  <div className="flex items-start gap-2 rounded-md bg-slate-50 border border-slate-200/70 p-2">
                    <PhoneCall size={12} className="text-violet-500 shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] text-slate-500 flex items-center gap-1.5">
                        <span className="uppercase font-semibold">
                          {data.last_call.direction} Call
                        </span>
                        <span>· {fmtDate(data.last_call.at)}</span>
                      </div>
                      <div className="text-[11px] text-slate-600 mt-0.5">
                        {data.last_call.status}
                      </div>
                    </div>
                  </div>
                )}
                {!data.last_message?.at && !data.last_call?.at && (
                  <div className="text-[11px] text-slate-400 italic">No recent activity</div>
                )}
              </div>
            </div>
          </div>
        )}
      </SheetContent>
      <UnsubscribeDialog
        open={showUnsubscribe}
        onOpenChange={setShowUnsubscribe}
        contactId={contactId}
        contactName={data?.name || initialName || ''}
        locationId={locationId}
      />
    </Sheet>
  );
}

export default PatientLookupSlider;
